// Canonical Markur Privacy Policy — this file IS the source of truth.
// Referenced by utils/legal.ts (PRIVACY_URL + PRIVACY_VERSION) in markurhealth/app.
// When editing: bump the effective/updated date rendered below. If the change
// materially affects user rights or the processor list, also bump PRIVACY_VERSION
// in the app repo so new sign-ups stamp the new version.
import { createFileRoute, Link } from '@tanstack/react-router'
import { Logo } from '@/components/Logo'

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicy,
})

function PrivacyPolicy() {
  return (
    <main className="bg-muted min-h-screen py-12">
      <header className="mx-auto mb-12 max-w-3xl px-8">
        <Link to="/" aria-label="Go to homepage">
          <Logo size="md" />
        </Link>
      </header>

      <article className="mx-auto max-w-3xl px-8">
        <h1 className="text-foreground mb-4 text-4xl font-bold">Privacy Policy</h1>
        <p className="text-muted-foreground mb-12 text-sm">
          Effective date: 16 July 2026 &middot; Last updated: 14 October 2026
        </p>

        <div className="text-foreground space-y-10">
          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">1. Who we are</h2>
            <p>
              This Privacy Policy describes how <strong>Markur Health Limited</strong> ("Markur",
              "we", "us", "our") collects and uses your personal data when you use the Markur mobile
              application ("the App").
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4 font-medium">Legal entity</td>
                    <td className="py-2">Markur Health Limited</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">Company type</td>
                    <td className="py-2">Private Company Limited by Shares (Ireland)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">CRO company number</td>
                    <td className="py-2">817464</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">Registered office</td>
                    <td className="py-2">
                      Apartment 610, Domville Crescent, Cherrywood, Dublin 18, D18 WRW7, Ireland
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">Data controller</td>
                    <td className="py-2">Markur Health Limited</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">Contact for privacy matters</td>
                    <td className="py-2">
                      <a href="mailto:support@markurhealth.com" className="font-medium underline">
                        support@markurhealth.com
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Markur Health Limited is the <strong>data controller</strong> for all personal data
              processed through the App under Regulation (EU) 2016/679 ("GDPR") and the Irish Data
              Protection Act 2018.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">2. Plain-language summary</h2>
            <p>Before the legal detail, here is what you need to know:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                We collect health data you enter (lab results, biomarkers) and information you share
                with our AI assistant. This is <strong>sensitive personal data</strong> under EU
                law.
              </li>
              <li>
                We <strong>require your explicit consent</strong> to process it. You can withdraw
                consent at any time by deleting your account.
              </li>
              <li>
                We use <strong>Supabase</strong> (hosted in Ireland) to store your data,{' '}
                <strong>Google Vertex AI (Gemini)</strong> — running in the EU — to power the AI
                chat and to read your lab-report uploads, and <strong>Resend</strong> to send
                transactional emails.
              </li>
              <li>
                We <strong>do not sell your data</strong>. We{' '}
                <strong>do not use it for advertising</strong>.
              </li>
              <li>
                You have strong rights under GDPR: access, correction, deletion, data export, and
                complaint to the Irish Data Protection Commission.
              </li>
              <li>
                The App is for users <strong>aged 16 and over</strong>.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">3. Personal data we collect</h2>

            <h3 className="text-xl font-semibold">
              3.1 Account and profile data (Article 6 GDPR — contract / consent)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left dark:border-neutral-700">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Data
                    </th>
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Source
                    </th>
                    <th scope="col" className="py-2 font-semibold">
                      Purpose
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4">Email address</td>
                    <td className="py-2 pr-4">Google Sign-In</td>
                    <td className="py-2">Account identity, support communication</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Full name</td>
                    <td className="py-2 pr-4">Google Sign-In / you</td>
                    <td className="py-2">Personalisation</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Profile photo</td>
                    <td className="py-2 pr-4">Google Sign-In (optional)</td>
                    <td className="py-2">Personalisation</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Date of birth</td>
                    <td className="py-2 pr-4">You</td>
                    <td className="py-2">Age-appropriate reference ranges, age-gate (16+)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Sex assigned at birth</td>
                    <td className="py-2 pr-4">You</td>
                    <td className="py-2">Sex-specific reference ranges for biomarkers</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Country / timezone</td>
                    <td className="py-2 pr-4">You / device</td>
                    <td className="py-2">Localisation, unit preferences</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold">
              3.2 Health data (Article 9 GDPR — explicit consent required)
            </h3>
            <p>
              This data is classified as <strong>special category personal data</strong> under
              Article 9 GDPR. We process it only with your <strong>explicit consent</strong>{' '}
              (Article 9(2)(a)), captured via a tickbox in the sign-in drawer before you
              authenticate with Google.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left dark:border-neutral-700">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Data
                    </th>
                    <th scope="col" className="py-2 font-semibold">
                      Purpose
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4">
                      Laboratory biomarker results (40+ markers across 11 health categories,
                      including hormones, cardiovascular, metabolic, kidney, liver, haematologic,
                      inflammation, iron, vitamins, body composition)
                    </td>
                    <td className="py-2">
                      Display trends, compute health scores, generate insights
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Uploaded lab-report documents (PDF or image)</td>
                    <td className="py-2">Automated extraction of biomarker values</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      Calculated health scores (overall wellness, biological age, cardiovascular,
                      hormone, inflammation, kidney, liver, metabolism, nutrition)
                    </td>
                    <td className="py-2">Dashboard and insights display</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      AI chat transcripts (messages you send and receive)
                    </td>
                    <td className="py-2">Conversational history and context</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      Health insights and notifications generated by the App
                    </td>
                    <td className="py-2">In-app display</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">User biomarker preferences (favourites, goals)</td>
                    <td className="py-2">Personalisation</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold">3.3 Family-sharing data</h3>
            <p>If you use the Family feature:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Invitation recipient email addresses and relationship labels</li>
              <li>Invitation tokens and acceptance status</li>
              <li>Granular per-biomarker sharing permissions you set for each family member</li>
            </ul>
            <p>
              <strong>Important:</strong> when you accept a family connection and share biomarker
              data, the data you share becomes visible to the other user under the permissions you
              set. You are responsible for only sharing data with people you trust.
            </p>

            <h3 className="text-xl font-semibold">3.4 App usage data</h3>
            <ul className="list-disc space-y-2 pl-6">
              <li>User settings (theme, chart timeframe, notification preferences)</li>
              <li>
                System audit logs (operation type, success/failure, timestamp) — retained for 12
                months for security and abuse prevention
              </li>
            </ul>

            <h3 className="text-xl font-semibold">3.5 Support communications</h3>
            <p>
              If you contact support@markurhealth.com or submit a support form in-app, we process
              the content of your message and any attachments you provide.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">4. Legal bases we rely on</h2>
            <p>Under GDPR we must have a lawful basis for each processing activity.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left dark:border-neutral-700">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Processing activity
                    </th>
                    <th scope="col" className="py-2 font-semibold">
                      Lawful basis
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4">
                      Providing the App to you (account, dashboard, data entry)
                    </td>
                    <td className="py-2">Contract performance — Art. 6(1)(b)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      Processing your <strong>health data</strong> (biomarkers, lab reports, AI chat
                      on health topics)
                    </td>
                    <td className="py-2">
                      <strong>Explicit consent</strong> — Art. 9(2)(a)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      Sending transactional emails (invitations, support replies, account deletion
                      confirmations)
                    </td>
                    <td className="py-2">Contract performance — Art. 6(1)(b)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Security logging, abuse prevention, audit trail</td>
                    <td className="py-2">Legitimate interests — Art. 6(1)(f)</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      Responding to legal requests or enforcing our Terms
                    </td>
                    <td className="py-2">
                      Legal obligation / legitimate interests — Art. 6(1)(c) / (f)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              <strong>You can withdraw your consent at any time</strong> by deleting your account in
              Settings &rarr; Account &rarr; Delete Account. Withdrawal will not affect the
              lawfulness of processing before the withdrawal.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">
              5. How we share your data (third-party processors)
            </h2>
            <p>
              We use the following third parties to operate the App. Each acts as a{' '}
              <strong>data processor</strong> under a written agreement with us. We do{' '}
              <strong>not</strong> sell your personal data.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left dark:border-neutral-700">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Processor
                    </th>
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Role
                    </th>
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Data shared
                    </th>
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Location
                    </th>
                    <th scope="col" className="py-2 font-semibold">
                      Safeguards
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Supabase Inc.</strong>
                    </td>
                    <td className="py-2 pr-4">
                      Database, authentication, file storage, edge functions
                    </td>
                    <td className="py-2 pr-4">
                      All account, profile, biomarker, chat, and family data
                    </td>
                    <td className="py-2 pr-4">
                      <strong>Ireland (EU region)</strong>
                    </td>
                    <td className="py-2">GDPR-compliant DPA; encryption at rest and in transit</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Google LLC (Google Sign-In)</strong>
                    </td>
                    <td className="py-2 pr-4">OAuth authentication</td>
                    <td className="py-2 pr-4">Email, name, profile photo</td>
                    <td className="py-2 pr-4">United States</td>
                    <td className="py-2">
                      Standard Contractual Clauses (SCCs); EU-U.S. Data Privacy Framework
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Google LLC (Vertex AI — Gemini 3.1 Flash-Lite)</strong>
                    </td>
                    <td className="py-2 pr-4">
                      Extraction of biomarker values from uploaded lab-report images or PDFs
                    </td>
                    <td className="py-2 pr-4">
                      Lab-report image/PDF content (transient — deleted from Supabase Storage within
                      1 hour of processing)
                    </td>
                    <td className="py-2 pr-4">
                      <strong>European Union (EU multi-region)</strong>
                    </td>
                    <td className="py-2">
                      In-region EU processing; Cloud Data Processing Addendum (CDPA); Section 17
                      Training Restriction — data not used to train models; zero data retention
                      eligible
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Google LLC (Vertex AI — Gemini)</strong>
                    </td>
                    <td className="py-2 pr-4">AI chat inference</td>
                    <td className="py-2 pr-4">
                      Chat messages, summarised biomarker context for relevance
                    </td>
                    <td className="py-2 pr-4">
                      <strong>European Union (EU multi-region)</strong>
                    </td>
                    <td className="py-2">
                      In-region EU processing; Cloud Data Processing Addendum (CDPA); Section 17
                      Training Restriction — data not used to train models
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Resend (Resend, Inc.)</strong>
                    </td>
                    <td className="py-2 pr-4">
                      Transactional email delivery (family invitations, support replies, account
                      deletion confirmations)
                    </td>
                    <td className="py-2 pr-4">Recipient email address, email content</td>
                    <td className="py-2 pr-4">United States</td>
                    <td className="py-2">SCCs; EU-U.S. Data Privacy Framework</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Mixpanel, Inc.</strong>
                    </td>
                    <td className="py-2 pr-4">
                      Product analytics — screen views and tap events (opt-in only, see &sect;5.3)
                    </td>
                    <td className="py-2 pr-4">
                      Event names + non-sensitive properties (no biomarker values, chat contents,
                      email, DOB, or health data). Anonymous app-generated identifiers only.
                    </td>
                    <td className="py-2 pr-4">
                      European Union (Frankfurt — Mixpanel EU data residency)
                    </td>
                    <td className="py-2">
                      GDPR-compliant DPA; EU data centre; SDK not initialised until consent granted
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>Sentry, Inc. (Functional Software, Inc.)</strong>
                    </td>
                    <td className="py-2 pr-4">
                      Crash reports — anonymised error details when the App crashes (opt-in only,
                      see &sect;5.3)
                    </td>
                    <td className="py-2 pr-4">
                      Exception messages (with health data scrubbed), device model, OS version, app
                      version, opaque user ID. No biomarker values, chat contents, or PII.
                    </td>
                    <td className="py-2 pr-4">European Union (Frankfurt)</td>
                    <td className="py-2">
                      GDPR-compliant DPA; EU data centre; SDK not initialised until consent granted;
                      JS-layer PII scrubbing before events leave the device
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <strong>RevenueCat, Inc.</strong>
                    </td>
                    <td className="py-2 pr-4">
                      Subscription and in-app purchase management; entitlement synchronisation
                    </td>
                    <td className="py-2 pr-4">
                      A pseudonymous app user identifier (your Supabase account ID) and your
                      subscription / purchase status. No name, email, biomarker values, chat
                      contents, or health data.
                    </td>
                    <td className="py-2 pr-4">United States</td>
                    <td className="py-2">
                      Standard Contractual Clauses (SCCs); GDPR-compliant DPA
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold">5.1 Lab-report uploads — important note</h3>
            <p>
              When you upload a lab report image or PDF for automatic parsing, your file is stored
              temporarily in encrypted <strong>Supabase Storage (Ireland, EU)</strong> while it is
              processed. The file is sent to{' '}
              <strong>Google Vertex AI (Gemini 3.1 Flash-Lite)</strong> running in{' '}
              <strong>the EU multi-region</strong> for text extraction — your lab-report data does
              not leave the European Union at any point during this process. The file is permanently
              deleted from Supabase Storage within <strong>1 hour</strong> of processing by an
              automated cleanup job. Only the extracted biomarker values are saved to your account.
              Under Google Cloud's Service Specific Terms (Section 17, Training Restriction), your
              data is not used to train or fine-tune any AI/ML models. You can enter biomarker
              values manually instead of uploading files if you prefer not to involve Vertex AI.
            </p>

            <h3 className="text-xl font-semibold">5.2 AI chat — important note</h3>
            <p>
              Messages you send in the AI chat, together with a summary of your relevant biomarkers
              for context, are transmitted to <strong>Google Vertex AI (Gemini)</strong> running in{' '}
              <strong>the EU multi-region</strong> for inference, and the assistant's response is
              streamed back to you — this data is processed within the European Union and does not
              leave the EEA. The full conversation is stored in your account in our Supabase
              database.{' '}
              <strong>
                Do not share information in the AI chat that you do not want transmitted to Google's
                Vertex AI service.
              </strong>
            </p>

            <h3 className="text-xl font-semibold">5.3 Analytics and crash reports — opt-in only</h3>
            <p>
              Both <strong>Mixpanel</strong> (product analytics) and <strong>Sentry</strong> (crash
              reports) are strictly opt-in. Their SDKs are <strong>not initialised</strong> until
              you have granted consent via the onboarding consent screen or later via{' '}
              <strong>Settings &rarr; Data &amp; Privacy &rarr; Usage data</strong>. If you decline
              or leave the toggles off, no events or crash telemetry are transmitted from your
              device.
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                Both settings default to OFF, per Article 5(3) of the ePrivacy Directive and CJEU{' '}
                <em>Planet49</em> (Case C-673/17, 2019).
              </li>
              <li>
                You can withdraw consent at any time from Settings &rarr; Data &amp; Privacy &rarr;
                Usage data. Withdrawal takes effect immediately: the Mixpanel SDK is disabled and
                any queued events are purged from your device; the Sentry client is closed.
              </li>
              <li>
                Withdrawal does not affect data that was lawfully processed before your withdrawal.
              </li>
              <li>
                The date and version of your consent are recorded on your account for our audit
                trail (GDPR Article 7(1)).
              </li>
            </ul>

            <h3 className="text-xl font-semibold">5.4 Third-party connectors you authorise</h3>
            <p>
              Markur lets you connect third-party applications (for example, AI assistants) to your
              account so they can receive your health data. These connections are{' '}
              <strong>optional and off by default</strong>; nothing is shared until you explicitly
              authorise a specific connector on Markur's consent screen.
            </p>
            <p>
              Unlike the processors listed above, a connector you authorise is an{' '}
              <strong>independent data controller</strong>. It receives your data for its own
              purposes and decides how to use it under its own privacy policy and terms, which
              Markur does not control. A connector may process your data{' '}
              <strong>outside the EEA</strong> — including in countries without an EU adequacy
              decision — and may use it in ways Markur does not permit for its own processors, such
              as to train AI models.
            </p>
            <p>
              Because we hold no data-processing agreement with a connector you choose, the lawful
              basis for this sharing is your <strong>explicit consent</strong> (Article 9(2)(a)),
              and the transfer relies on your explicit consent to it under{' '}
              <strong>Article 49(1)(a)</strong>, given after being informed of the risks. We record
              the date and version of each connector consent. You can disconnect a connector at any
              time in <strong>Settings &rarr; Connectors</strong>; this stops future sharing but
              cannot recall data already sent, and does not delete your account.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">6. International transfers</h2>
            <p>
              Your data is primarily stored in the <strong>European Union (Ireland)</strong> via
              Supabase. Some processors are based in the <strong>United States</strong> (Google
              Sign-In for OAuth authentication, Resend for transactional email, and RevenueCat for
              subscription management). Both AI features — AI chat and lab-report parsing — run on
              Google Vertex AI entirely within the EU multi-region, so{' '}
              <strong>no Article 9 health data transits outside the EEA</strong> for these features.
              For all transfers outside the EEA we rely on:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                The European Commission's <strong>Standard Contractual Clauses (SCCs)</strong> as
                required by Article 46 GDPR;
              </li>
              <li>
                Participation in the <strong>EU-U.S. Data Privacy Framework</strong> where
                applicable;
              </li>
              <li>
                The <strong>UK International Data Transfer Addendum</strong> for UK users.
              </li>
            </ul>
            <p>
              You can request a copy of the safeguards in place by emailing
              support@markurhealth.com.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">7. How long we keep your data</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left dark:border-neutral-700">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Category
                    </th>
                    <th scope="col" className="py-2 font-semibold">
                      Retention
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4">Profile, biomarker, chat, family data</td>
                    <td className="py-2">Until you delete your account</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">AI chat history</td>
                    <td className="py-2">
                      Retained until you delete individual sessions or your account
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Uploaded lab-report files</td>
                    <td className="py-2">
                      Stored encrypted in Supabase Storage (Ireland, EU) for up to 1 hour during
                      processing, then permanently deleted by automated cleanup
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">System audit logs</td>
                    <td className="py-2">12 months</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Support emails</td>
                    <td className="py-2">24 months from last correspondence</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Backups</td>
                    <td className="py-2">Up to 30 days after deletion, then permanently purged</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              When you delete your account, we perform a <strong>hard deletion</strong> of your
              personal data within our systems. Copies may persist in encrypted backups for up to 30
              days before being overwritten.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">8. Your rights under GDPR</h2>
            <p>You have the following rights in relation to your personal data:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Right of access (Art. 15)</strong> — request a copy of the data we hold
                about you.
              </li>
              <li>
                <strong>Right to rectification (Art. 16)</strong> — ask us to correct inaccurate
                data.
              </li>
              <li>
                <strong>Right to erasure (Art. 17)</strong> — ask us to delete your data ("right to
                be forgotten").
              </li>
              <li>
                <strong>Right to restriction (Art. 18)</strong> — ask us to stop using your data in
                certain ways.
              </li>
              <li>
                <strong>Right to data portability (Art. 20)</strong> — receive your data in a
                machine-readable format. You can export all your data directly from{' '}
                <strong>Settings &rarr; Data &amp; Privacy &rarr; Export my data</strong>. See
                &sect;8.1 below for what the export contains.
              </li>
              <li>
                <strong>Right to object (Art. 21)</strong> — object to processing based on
                legitimate interests.
              </li>
              <li>
                <strong>Right to withdraw consent (Art. 7)</strong> — withdraw the explicit consent
                you gave for health-data processing; this will result in account deletion because
                the App cannot function without that consent.
              </li>
              <li>
                <strong>
                  Right not to be subject to solely automated decision-making (Art. 22)
                </strong>{' '}
                — our health scores are informational only and do not produce legal effects.
              </li>
            </ul>

            <h3 className="text-xl font-semibold">
              8.1 What the self-service data export contains
            </h3>
            <p>
              The export (JSON file, downloaded from{' '}
              <strong>Settings &rarr; Data &amp; Privacy &rarr; Export my data</strong>) includes
              all personal data we hold about you across the following categories:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 text-left dark:border-neutral-700">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Category
                    </th>
                    <th scope="col" className="py-2 font-semibold">
                      What's included
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  <tr>
                    <td className="py-2 pr-4">Profile</td>
                    <td className="py-2">
                      Name, email, date of birth, sex, country, photo URL, consent timestamps,
                      onboarding state
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Biomarker results</td>
                    <td className="py-2">
                      All lab values you have entered or uploaded, with dates and units
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Health scores</td>
                    <td className="py-2">
                      Calculated overall wellness, biological age, and pillar scores
                      (cardiovascular, hormone, inflammation, kidney, liver, metabolism, nutrition)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Insights and notifications</td>
                    <td className="py-2">Health insights generated by the App for your account</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">AI chat</td>
                    <td className="py-2">
                      Full conversation history (sessions and messages), message feedback (thumbs
                      up/down)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Family data</td>
                    <td className="py-2">
                      Family connections, sharing permissions, and invitations (sent and received)
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Preferences and settings</td>
                    <td className="py-2">
                      App settings, biomarker preferences, notification preferences
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Processing jobs</td>
                    <td className="py-2">
                      Transient lab-report parsing job records (24-hour retention; typically empty
                      after processing completes)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              The export covers both <strong>Art. 20 portability</strong> (data you provided or that
              was generated by your use of the service) and <strong>Art. 15 access</strong> (all
              personal data we hold about you).
            </p>
            <p>
              Data not in the self-service export (available on request via Subject Access Request
              at <strong>support@markurhealth.com</strong>):
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Security audit log</strong> (<code>system_audit_log</code>): operation type,
                success/failure, and timestamp entries retained for 12 months under legitimate
                interests (Art. 6(1)(f)). Outside Art. 20 portability scope per EDPB guidelines.
              </li>
              <li>
                <strong>Lab-report processing log</strong> (<code>lab_parse_log</code>): internal
                log of lab upload events, parse status, and matched biomarker names. Processing
                infrastructure metadata, not user-provided content. Outside Art. 20 scope.
              </li>
              <li>
                <strong>AI infrastructure telemetry</strong> (<code>chat_log</code>): model name,
                token counts, latency, and error messages. Operational metrics not constituting user
                content. No user-facing access control.
              </li>
            </ul>

            <h3 className="text-xl font-semibold">How to exercise your rights</h3>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                In-app: <strong>Settings &rarr; Data &amp; Privacy &rarr; Export my data</strong> to
                export your data, and <strong>Settings &rarr; Account &rarr; Delete Account</strong>{' '}
                to delete your account.
              </li>
              <li>
                Self-serve on the web: you can also delete your account without the app installed by
                following the instructions at{' '}
                <Link to="/delete-account" className="font-medium underline">
                  markurhealth.com/delete-account
                </Link>
                .
              </li>
              <li>
                By email:{' '}
                <a href="mailto:support@markurhealth.com" className="font-medium underline">
                  support@markurhealth.com
                </a>{' '}
                — include "Subject Access Request" in the subject line for formal data-access
                requests.
              </li>
            </ul>
            <p>
              We respond to data-subject requests within <strong>30 days</strong> as required by
              GDPR.
            </p>

            <h3 className="text-xl font-semibold">Right to lodge a complaint</h3>
            <p>You have the right to complain to the Irish Data Protection Commission:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Data Protection Commission</strong>, 21 Fitzwilliam Square South, Dublin 2,
                D02 RD28, Ireland
              </li>
              <li>
                Website:{' '}
                <a
                  href="https://www.dataprotection.ie"
                  className="font-medium underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  dataprotection.ie
                </a>
              </li>
              <li>Phone: +353 578 684 800</li>
            </ul>
            <p>
              If you are in another EU/EEA country, you may lodge a complaint with your local
              supervisory authority.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">9. Security</h2>
            <p>We implement appropriate technical and organisational measures, including:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Encryption of data in transit (TLS) and at rest;</li>
              <li>
                Row-level security in our database so that users can only access their own data;
              </li>
              <li>OAuth-based authentication with no passwords stored in our systems;</li>
              <li>Rate limiting on sensitive operations (such as lab-report parsing);</li>
              <li>Audit logging of security-relevant operations;</li>
              <li>Access controls for our team members.</li>
            </ul>
            <p>
              No system is perfectly secure. In the event of a personal-data breach that is likely
              to result in a risk to your rights, we will notify the Data Protection Commission
              within 72 hours and notify affected users without undue delay.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">10. Children</h2>
            <p>
              The App is <strong>not intended for children under 16</strong>. We do not knowingly
              collect data from children under 16. If you sign in with a Google account and a brief
              age-and-consent form shows a date of birth that indicates you are under 16, your
              newly-created account is deleted from our systems immediately. We keep only an
              anonymised record (no name, no email, no date of birth) for auditing purposes.
            </p>
            <p>
              Users aged 16 and over may use the App under the Irish age-of-consent rules for
              information-society services (Data Protection Act 2018, s.31).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">11. Not medical advice</h2>
            <p>
              Markur is a <strong>wellness application</strong>. It is{' '}
              <strong>not a medical device</strong> and does not provide medical diagnosis,
              treatment, or professional medical advice. Health scores, insights, and AI-chat
              responses are for informational and educational purposes only. Always consult a
              licensed healthcare professional for medical decisions.
            </p>
            <p>This notice is repeated in the Terms of Service.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">12. Cookies and tracking</h2>
            <p>The mobile App does not use cookies.</p>
            <p>
              The App can use <strong>Mixpanel EU</strong> (product analytics) and{' '}
              <strong>Sentry EU</strong> (crash reports), but only if you opt in — see &sect;5.3. If
              you decline or leave the settings off, no SDK reads or writes on your device happen.
              The App does not use third-party advertising, nor cross-app tracking, nor data
              brokers. Basic app performance metrics may be captured by Supabase and by Google Play
              under their respective policies.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">13. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we
              will notify you in the App and by email before the changes take effect. The current
              version and effective date are shown at the top of this page.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">14. Contact</h2>
            <p>Questions about this Privacy Policy or our processing of your personal data:</p>
            <p className="text-muted-foreground">
              <strong>Markur Health Limited</strong>
              <br />
              Apartment 610, Domville Crescent, Cherrywood
              <br />
              Dublin 18, D18 WRW7, Ireland
              <br />
              Email:{' '}
              <a href="mailto:support@markurhealth.com" className="font-medium underline">
                support@markurhealth.com
              </a>
            </p>
          </section>
        </div>
      </article>
    </main>
  )
}
